import React, { useContext, useEffect, useState } from 'react';
import {
  AnsweringSubscriptionJsonType,
  AnsweringSubscriptionLevel,
} from '@bb/domain/dist/types';
import type {
  GetPortalAnsweringConfigResponse,
  AnsweringServicePayer,
  ISubscribeAnsweringServiceBody,
} from '@/app-package';
import { AnsweringServiceType } from '@bb/domain/dist/types/AnsweringSubscription';
import { findActiveSubscription } from 'api/AnsweringSubscriptionController';
import { createAnsweringServiceSubscription } from 'api/AnsweringServiceController';
import { useAnsweringServiceDetails } from 'hooks';
import { getEnabledListingCountForAppointmentDesk } from 'api/ListingController';
import { AuthContext } from 'contexts';
import { message } from '@bb/elements-web';
import { StyledLoading } from './StyledComponents';

/** The four steps a user or brokerage goes through when
 *  signing up for a new Answering Service subscription. */
export enum AnsweringServiceSubscribeStep {
  LearnMore = 1,
  ManageListings = 2,
  PaymentConfirmation = 3,
  SubscriptionSuccess = 4,
}

// Define the shape for our context
export type AnsweringServiceContextType = {
  /** The user/brokerage's already active Answering Service subscription, if one exists. */
  answeringSubscription?: Omit<AnsweringSubscriptionJsonType, 'startDate'> & {
    startDate: Date;
  };
  /** A method that redirects the browser to whatever page the user can manage their billing at.
   *  Used at the end of the flow for creating a new subscription. */
  billingRedirection: () => void;
  /** The method to trigger the Answering Service subscription creation in the
   * backend and then move the user to the next screen in the flow afterwards. */
  createSubscription: (
    billingProfile: ISubscribeAnsweringServiceBody['billingProfile'],
  ) => void;
  /** Which of the four steps of the Answering Service subscription process the user is on. */
  currentStep: number;
  /** The type of Answering Service this user currently actively has. */
  currentSubscriptionType: AnsweringServiceType | null;
  /** Whether this user's preference is to have Answering Service turned on for all new listings they receive by default. */
  defaultEnabled: boolean;
  /** The number of listings this user or brokerage has which have Answering Service enabled. */
  enabledListingsCount: number;
  /** Whether the user has an active Answering Service subscription or is currently unsubscribed. */
  hasActiveAnsweringService: boolean;
  /** The Onboard Portal configurations this user's MLS has specific to their Answering Service setup. */
  portalConfig?: GetPortalAnsweringConfigResponse;
  /** The method for setting the current step in the subscription flow.
   * Exported so that other pages can directly call it. */
  setCurrentStep: React.Dispatch<React.SetStateAction<number>>;
  /** The method for setting the user's preference for automatically turning on
   * Answering Service for new listings they receive by default.
   * Defined here so both this Context and the ListingTable can set it. */
  setDefaultEnabled: React.Dispatch<React.SetStateAction<boolean>>;
  /** Whether the Subscribe btn should be unclickable and display loading because the backend is creating a new subscription. */
  subscribeBtnLoading?: boolean;
  /** Who pays for this Answering Service subscription. */
  subscriptionPayer: AnsweringServicePayer;
  /** The user viewing this page. */
  userId?: string;
  /** Whether it is a user or a brokerage going through this flow. */
  viewingLevel: AnsweringSubscriptionLevel;
};

// Define the props the provider takes in
interface AnsweringServiceContextProviderProps {
  /** The ID for this user's brokerage. */
  orgId: string;
  /** Whether it is a user or a brokerage going through this flow. */
  viewingLevel: AnsweringSubscriptionLevel;
  /** The ID of this user. */
  userId?: string;
  /** A method that redirects the browser to whatever page the user can manage their billing at.
   *  Will be used at the end of a new subscription flow & with back buttons. */
  billingRedirection: () => void;
}

/** The Context for all values shared across multiple pages during the Answering Service subscription flow. */
const AnsweringServiceContext =
  React.createContext<AnsweringServiceContextType>(null!);

// Create the actual provider with all the values it will export
export const AnsweringServiceContextProvider: React.FC<
  AnsweringServiceContextProviderProps
> = ({ orgId, viewingLevel, userId, billingRedirection, children }) => {
  const { answeringPortalConfig } = useContext(AuthContext);

  // Answering Service subscription
  const [answeringSubscription, setAnsweringSubscription] =
    useState<AnsweringServiceContextType['answeringSubscription']>();
  const [fetchingSubscription, setFetchingSubscription] = useState(true);

  // Frontend component states
  // (See definitions in the AnsweringServiceContextType above)
  const [currentSubscriptionType, setCurrentSubscriptionType] =
    useState<AnsweringServiceType>(null);
  const [currentStep, setCurrentStep] =
    useState<AnsweringServiceSubscribeStep>(1); // Default them to the first step in the process.
  const [defaultEnabled, setDefaultEnabled] = useState(true);
  const [enabledListingsCount, setEnabledListingsCount] = useState(0);
  const [hasActiveAnsweringService, setHasActiveAnsweringService] =
    useState(false);
  const [subscriptionPayer, setSubscriptionPayer] =
    useState<AnsweringServicePayer>(null);
  const [subscribeBtnLoading, setSubscribeBtnLoading] = useState(false);

  // Grab all the Answering Service values from the shared hook code,
  // then set them here to be re-exported.
  const {
    answeringServiceType: fetchedServiceType,
    answeringServicePayer: fetchedServicePayer,
    hasActiveAnsweringService: fetchedActiveStatus,
  } = useAnsweringServiceDetails({ viewingLevel, userId });
  useEffect(() => {
    setSubscriptionPayer(fetchedServicePayer);
    setCurrentSubscriptionType(fetchedServiceType);
    setHasActiveAnsweringService(fetchedActiveStatus);
  }, [fetchedServiceType, fetchedServicePayer, fetchedActiveStatus]);

  // To be used when calculating the total that the user/brokerage has to pay. (Currently unused.)
  useEffect(() => {
    /** Go grab the number of listings this agent/brokerage has with Answering Service activated. */
    const fetchEnabledCount = async () => {
      try {
        const enabled =
          await getEnabledListingCountForAppointmentDesk(viewingLevel);
        setEnabledListingsCount(enabled.count);
      } catch (err) {
        message.error(
          'Error fetching count of listings with Answering Service enabled',
        );
      }
    };

    // Only calculate when this is a user paying and they are on the PaymentConfirmation step
    if (
      viewingLevel === 'user' &&
      currentStep === AnsweringServiceSubscribeStep.PaymentConfirmation
    ) {
      fetchEnabledCount();
    }
  }, [currentStep, viewingLevel]);

  useEffect(() => {
    /** Method to fetch the user/brokerage's currently-existing subscription if they have one
     *  and push them to the proper step in the flow if they do. */
    const fetchSubscription = async () => {
      try {
        const subscriptionData = await findActiveSubscription({
          subscriptionLevel: viewingLevel,
          userId,
        });
        if (subscriptionData) {
          // If they have an active existing subscription, set the data
          setHasActiveAnsweringService(true);
          setAnsweringSubscription({
            ...subscriptionData,
            startDate: new Date(subscriptionData.startDate),
          });
          setDefaultEnabled(subscriptionData.defaultEnabled !== false);
          // Send them to the ManageListings step if they already have a sub
          setCurrentStep(AnsweringServiceSubscribeStep.ManageListings);
        }
      } catch (err) {
        message.error('Error fetching active subscription');
      }
      setFetchingSubscription(false);
    };

    fetchSubscription();
  }, [viewingLevel, orgId, userId, answeringPortalConfig]);

  /**
   * The method to trigger the Answering Service subscription creation in the
   * backend and then move the user to the next screen in the flow afterwards.
   * */
  const createSubscription = async (
    billingProfile: ISubscribeAnsweringServiceBody['billingProfile'],
  ) => {
    setSubscribeBtnLoading(true);

    try {
      await createAnsweringServiceSubscription({
        subscriptionLevel: viewingLevel,
        billingProfile,
      });

      setCurrentStep(AnsweringServiceSubscribeStep.SubscriptionSuccess);
      setHasActiveAnsweringService(true);
    } catch (err) {
      message.error('Error subscribing to Live Concierge Service');
    }
    setSubscribeBtnLoading(false);
  };

  /** The values the context exports */
  if (fetchingSubscription) {
    return StyledLoading;
  }
  return (
    <AnsweringServiceContext.Provider
      value={{
        answeringSubscription,
        billingRedirection,
        createSubscription,
        currentSubscriptionType,
        currentStep,
        defaultEnabled,
        enabledListingsCount,
        hasActiveAnsweringService,
        portalConfig: answeringPortalConfig,
        setCurrentStep,
        setDefaultEnabled,
        subscribeBtnLoading,
        subscriptionPayer,
        userId,
        viewingLevel,
      }}
    >
      {children}
    </AnsweringServiceContext.Provider>
  );
};

export default AnsweringServiceContext;
